import { Box } from "@mui/material";
import { alpha, useTheme, type SxProps, type Theme } from "@mui/material/styles";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

type ScoutChatUploadFilesHandler = (files: File[]) => Promise<void>;

type ScoutChatDropzoneContextValue = {
  registerUploadFiles: (handler: ScoutChatUploadFilesHandler | null) => void;
};

const ScoutChatDropzoneContext = createContext<ScoutChatDropzoneContextValue | null>(null);

function hasFileDrag(event: React.DragEvent): boolean {
  return [...event.dataTransfer.types].includes("Files");
}

export function useRegisterScoutChatFileUpload(uploadFiles: ScoutChatUploadFilesHandler) {
  const context = useContext(ScoutChatDropzoneContext);

  useEffect(() => {
    if (!context) {
      return;
    }

    context.registerUploadFiles(uploadFiles);
    return () => {
      context.registerUploadFiles(null);
    };
  }, [context, uploadFiles]);
}

export function ScoutChatDropzone({ children, sx }: { children: ReactNode; sx?: SxProps<Theme> }) {
  const theme = useTheme();
  const uploadHandlerRef = useRef<ScoutChatUploadFilesHandler | null>(null);
  const dragDepthRef = useRef(0);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);

  const registerUploadFiles = useCallback((handler: ScoutChatUploadFilesHandler | null) => {
    uploadHandlerRef.current = handler;
  }, []);

  const cancelFileDrag = useCallback(() => {
    dragDepthRef.current = 0;
    setIsDraggingFiles(false);
  }, []);

  useEffect(() => {
    if (!isDraggingFiles) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      cancelFileDrag();
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [cancelFileDrag, isDraggingFiles]);

  const handleDragEnter = useCallback((event: React.DragEvent) => {
    if (!hasFileDrag(event)) {
      return;
    }

    event.preventDefault();
    dragDepthRef.current += 1;
    setIsDraggingFiles(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    if (!hasFileDrag(event)) {
      return;
    }

    event.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setIsDraggingFiles(false);
    }
  }, []);

  const handleDragOver = useCallback((event: React.DragEvent) => {
    if (!hasFileDrag(event)) {
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
  }, []);

  const handleDrop = useCallback(
    async (event: React.DragEvent) => {
      if (!hasFileDrag(event)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      cancelFileDrag();

      const files = [...event.dataTransfer.files];
      if (files.length === 0) {
        return;
      }

      await uploadHandlerRef.current?.(files);
    },
    [cancelFileDrag]
  );

  const handleDragEnd = useCallback(
    (event: React.DragEvent) => {
      if (!hasFileDrag(event)) {
        return;
      }

      cancelFileDrag();
    },
    [cancelFileDrag]
  );

  return (
    <ScoutChatDropzoneContext.Provider value={{ registerUploadFiles }}>
      <Box
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDrop={(event) => {
          void handleDrop(event);
        }}
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
          minHeight: 0,
          ...sx,
        }}
      >
        {children}
        {isDraggingFiles && (
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 4,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              border: 2,
              borderStyle: "dashed",
              borderColor: theme.palette.primary.main,
            }}
          />
        )}
      </Box>
    </ScoutChatDropzoneContext.Provider>
  );
}
